import { apiClient } from "../../../../lib/axios";

export interface CreateClientRequest {
  name: string;
  is_active: boolean;
  enable_anonymisation?: boolean;
  learning_locker_enabled?: boolean;
  occupational_health_enabled?: boolean;
  wearables_enabled?: boolean;
  akea_life_enabled?: boolean;
}

export interface EnabledServices {
  learning_locker: boolean;
  occupational_health: boolean;
  wearables: boolean;
  akea_life: boolean;
}

export interface ServiceOverrides {
  learning_locker: boolean | null;
  occupational_health: boolean | null;
  wearables: boolean | null;
  akea_life: boolean | null;
}

export interface ResetUserPasswordResponse {
  data: {
    user_id: string;
    email: string;
    temporary: boolean;
    send_email: boolean;
    email_sent: boolean;
    email_error: string | null;
    generated_password: string | null;
  };
}

export interface CreateClientResponse {
  data: {
    client: {
      id: string;
      name: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
      createdBy?: string | null;
      updatedBy?: string | null;
    };
    created_departments: number;
    created_job_roles: number;
  };
}

export interface CreateDepartmentRequest {
  name: string;
  is_active: boolean;
}

export interface CreateJobRoleRequest {
  name: string;
  is_active: boolean;
}

export interface ClientDepartment {
  id: string;
  name: string;
  is_active?: boolean;
}

export interface ClientJobRole {
  id: string;
  name: string;
  is_active?: boolean;
}

export interface ClientRegion {
  id: string;
  name: string;
  is_active?: boolean;
}

export interface CreateRegionRequest {
  name: string;
  is_active: boolean;
}

export interface ClientRegionsResponse {
  data: ClientRegion[];
}

export interface CentralRegionsResponse {
  data: ClientRegion[];
}

export interface ClientDepot {
  id: string;
  name: string;
  is_active?: boolean;
}

export interface CreateDepotRequest {
  name: string;
  is_active: boolean;
}

export interface ClientDepotsResponse {
  data: ClientDepot[];
}

export interface CentralDepotsResponse {
  data: ClientDepot[];
}

export interface DefaultRegionsResponse {
  data: ClientRegion[];
}

export interface ClientUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin?: boolean | null;
  requires_consent?: boolean | null;
  consent_status?: "required" | "completed" | "withdrawn" | null;
  first_connected_at?: string | null;
  wear_jeopardy_status?:
    | "not_connected"
    | "recently_connected"
    | "not_worn"
    | "intermittent_wear"
    | "wearing_consistently"
    | null;
  wear_jeopardy_status_reason?: string | null;
  wear_jeopardy_last_data_at?: string | null;
  departments: ClientDepartment[];
  job_roles: ClientJobRole[];
  regions: ClientRegion[];
  depots: ClientDepot[];
  enabled_services?: EnabledServices;
  service_overrides?: ServiceOverrides;
  integrations?: {
    provider: string;
    name: string;
    icon?: string | null;
    connected: boolean;
    needs_reauth?: boolean;
  }[];
}

export interface ClientUsersResponse {
  data: ClientUser[];
}

export interface ClientUserCountResponse {
  data: {
    count: number;
  };
}

export interface ClientJobRolesResponse {
  data: ClientJobRole[];
}

export interface ClientSummary {
  id: string;
  name: string;
  is_active?: boolean;
  isActive?: boolean;
  enable_anonymisation?: boolean;
  enableAnonymisation?: boolean;
  enable_user_management?: boolean;
  enableUserManagement?: boolean;
  learning_locker_enabled?: boolean;
  learningLockerEnabled?: boolean;
  occupational_health_enabled?: boolean;
  occupationalHealthEnabled?: boolean;
  wearables_enabled?: boolean;
  wearablesEnabled?: boolean;
  akea_life_enabled?: boolean;
  akeaLifeEnabled?: boolean;
  sso_enabled?: boolean;
  ssoEnabled?: boolean;
  sso_domains?: string | null;
  ssoDomains?: string | null;
}

export interface ClientDetails extends ClientSummary {
  qrCodeValue?: string | null;
  qrCodeUrl?: string | null;
}

export interface ClientDetailsResponse {
  data: ClientDetails | null;
}

export interface ClientsResponse {
  data: ClientSummary[];
}

export interface CentralUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  requires_consent?: boolean | null;
  consent_status?: "required" | "completed" | "withdrawn" | null;
  client?: ClientSummary | null;
  departments: ClientDepartment[];
  job_roles: ClientJobRole[];
  regions: ClientRegion[];
  depots: ClientDepot[];
  enabled_services?: EnabledServices;
  service_overrides?: ServiceOverrides;
  integrations?: {
    provider: string;
    name: string;
    icon?: string | null;
    connected: boolean;
    needs_reauth?: boolean;
  }[];
}

export interface CentralUsersResponse {
  data: CentralUser[];
}

export interface CentralDepartmentsResponse {
  data: ClientDepartment[];
}

export interface CentralJobRolesResponse {
  data: ClientJobRole[];
}

export interface ClientDashboardSetting {
  dashboard_key: string;
  is_enabled: boolean;
  ai_enabled?: boolean | null;
}

export interface ClientDashboardSettingsResponse {
  data: ClientDashboardSetting[];
}

export interface EmailTemplate {
  id: string;
  template_key: string;
  name: string;
  subject: string;
  html_body: string;
  text_body: string;
  is_active: boolean;
  updated_at: string;
}

export interface EmailTemplateResponse {
  data: EmailTemplate;
}

export interface DefaultDepartmentsResponse {
  data: ClientDepartment[];
}

export interface DefaultJobRolesResponse {
  data: ClientJobRole[];
}

export interface CreateSingleUserRequest {
  email: string;
  first_name: string;
  last_name: string;
  company?: string;
  requires_consent?: boolean;
  is_admin?: boolean;
  delivery?: "email" | "csv";
  login_url?: string;
  service_overrides?: Partial<ServiceOverrides>;
  department_ids?: string[];
  job_role_ids?: string[];
  region_ids?: string[];
  depot_ids?: string[];
}

export interface CreateSingleUserResponse {
  data: {
    user_id: string;
    email: string;
    first_name: string;
    last_name: string;
    keycloak_user_id: string;
    is_admin: boolean;
    password?: string;
    email_sent: boolean;
    email_error?: string;
  };
}

export interface BulkUploadUserInput {
  email: string;
  first_name: string;
  last_name: string;
  company?: string;
  requires_consent?: boolean;
}

export type BulkUploadStatus =
  | "ready"
  | "invalid"
  | "duplicate_in_file"
  | "exists_same_client"
  | "exists_other_client";

export interface BulkUploadPreviewRow {
  row_number: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string | null;
  requires_consent: boolean;
  status: BulkUploadStatus;
  message: string | null;
  existing_client_id: string | null;
  existing_client_name: string | null;
}

export interface BulkUploadSummary {
  total: number;
  ready: number;
  invalid: number;
  duplicate_in_file: number;
  exists_same_client: number;
  exists_other_client: number;
  will_insert: number;
}

export interface BulkUploadPreviewResponse {
  data: {
    rows: BulkUploadPreviewRow[];
    summary: BulkUploadSummary;
  };
}

export type BulkUploadDelivery = "email" | "csv";

export interface BulkUploadConfirmResponse {
  data: {
    summary: {
      total: number;
      ready: number;
      created: number;
      failed: number;
      skipped: number;
    };
    created: Array<{
      email: string;
      first_name: string;
      last_name: string;
      user_id: string;
      keycloak_user_id: string;
      email_sent: boolean;
      email_error?: string;
      password?: string;
    }>;
    failed: Array<{ email: string; error: string }>;
    skipped: Array<{
      row_number: number;
      email: string;
      status: BulkUploadStatus;
      reason: string | null;
    }>;
    password_csv?: string;
  };
}

export const clientManagementApi = {
  createClient: async (
    payload: CreateClientRequest
  ): Promise<CreateClientResponse> => {
    const response = await apiClient.post("/admin/clients", payload);
    return response.data;
  },

  listClients: async (): Promise<ClientsResponse> => {
    const response = await apiClient.get("/admin/clients");
    return response.data;
  },

  getClientDetails: async (
    clientId: string
  ): Promise<ClientDetailsResponse> => {
    const response = await apiClient.get(`/admin/clients/${clientId}`);
    return response.data;
  },

  updateClient: async (
    clientId: string,
    payload: Partial<CreateClientRequest>
  ) => {
    const response = await apiClient.put(`/admin/clients/${clientId}`, payload);
    return response.data;
  },

  deleteClient: async (clientId: string) => {
    const response = await apiClient.delete(`/admin/clients/${clientId}`);
    return response.data;
  },

  createDepartment: async (clientId: string, payload: CreateDepartmentRequest) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/departments`,
      payload
    );
    return response.data;
  },

  updateDepartment: async (
    departmentId: string,
    payload: Partial<CreateDepartmentRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/departments/${departmentId}`,
      payload
    );
    return response.data;
  },

  removeUserFromDepartment: async (departmentId: string, userId: string) => {
    const response = await apiClient.delete(
      `/admin/departments/${departmentId}/users/${userId}`
    );
    return response.data;
  },

  deleteDepartment: async (departmentId: string) => {
    const response = await apiClient.delete(`/admin/departments/${departmentId}`);
    return response.data;
  },

  createJobRole: async (clientId: string, payload: CreateJobRoleRequest) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/job-roles`,
      payload
    );
    return response.data;
  },

  listClientUsers: async (clientId: string): Promise<ClientUsersResponse> => {
    const response = await apiClient.get(`/admin/clients/${clientId}/users`);
    return response.data;
  },

  getClientUserCount: async (
    clientId: string
  ): Promise<ClientUserCountResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/user-count`
    );
    return response.data;
  },

  listJobRoles: async (clientId: string): Promise<ClientJobRolesResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/job-roles`
    );
    return response.data;
  },

  updateJobRole: async (
    jobRoleId: string,
    payload: Partial<CreateJobRoleRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/job-roles/${jobRoleId}`,
      payload
    );
    return response.data;
  },

  removeUserFromJobRole: async (jobRoleId: string, userId: string) => {
    const response = await apiClient.delete(
      `/admin/job-roles/${jobRoleId}/users/${userId}`
    );
    return response.data;
  },

  deleteJobRole: async (jobRoleId: string) => {
    const response = await apiClient.delete(`/admin/job-roles/${jobRoleId}`);
    return response.data;
  },

  listCentralUsers: async (
    clientId?: string | null
  ): Promise<CentralUsersResponse> => {
    const response = await apiClient.get("/admin/central/users", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listCentralDepartments: async (
    clientId?: string | null
  ): Promise<CentralDepartmentsResponse> => {
    const response = await apiClient.get("/admin/central/departments", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listCentralJobRoles: async (
    clientId?: string | null
  ): Promise<CentralJobRolesResponse> => {
    const response = await apiClient.get("/admin/central/job-roles", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listClientDashboardSettings: async (
    clientId: string
  ): Promise<ClientDashboardSettingsResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/dashboard-settings`
    );
    return response.data;
  },

  updateClientDashboardSetting: async (
    clientId: string,
    dashboardKey: string,
    payload: { is_enabled?: boolean; ai_enabled?: boolean | null }
  ) => {
    const response = await apiClient.put(
      `/admin/clients/${clientId}/dashboard-settings/${dashboardKey}`,
      payload
    );
    return response.data;
  },

  getEmailTemplate: async (
    templateKey: string
  ): Promise<EmailTemplateResponse> => {
    const response = await apiClient.get(
      `/admin/email-templates/${encodeURIComponent(templateKey)}`
    );
    return response.data;
  },

  updateEmailTemplate: async (
    templateKey: string,
    payload: {
      name?: string;
      subject: string;
      html_body: string;
      text_body: string;
      is_active?: boolean;
    }
  ): Promise<EmailTemplateResponse> => {
    const response = await apiClient.put(
      `/admin/email-templates/${encodeURIComponent(templateKey)}`,
      payload
    );
    return response.data;
  },

  listDefaultDepartments: async (): Promise<DefaultDepartmentsResponse> => {
    const response = await apiClient.get("/admin/default/departments");
    return response.data;
  },

  createDefaultDepartment: async (payload: CreateDepartmentRequest) => {
    const response = await apiClient.post("/admin/default/departments", payload);
    return response.data;
  },

  updateDefaultDepartment: async (
    departmentId: string,
    payload: Partial<CreateDepartmentRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/default/departments/${departmentId}`,
      payload
    );
    return response.data;
  },

  deleteDefaultDepartment: async (departmentId: string) => {
    const response = await apiClient.delete(
      `/admin/default/departments/${departmentId}`
    );
    return response.data;
  },

  listDefaultJobRoles: async (): Promise<DefaultJobRolesResponse> => {
    const response = await apiClient.get("/admin/default/job-roles");
    return response.data;
  },

  createDefaultJobRole: async (payload: CreateJobRoleRequest) => {
    const response = await apiClient.post("/admin/default/job-roles", payload);
    return response.data;
  },

  updateDefaultJobRole: async (
    jobRoleId: string,
    payload: Partial<CreateJobRoleRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/default/job-roles/${jobRoleId}`,
      payload
    );
    return response.data;
  },

  deleteDefaultJobRole: async (jobRoleId: string) => {
    const response = await apiClient.delete(
      `/admin/default/job-roles/${jobRoleId}`
    );
    return response.data;
  },

  assignUserToClient: async (clientId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/users/${userId}`
    );
    return response.data;
  },

  removeUserFromClient: async (userId: string) => {
    const response = await apiClient.delete(`/admin/clients/users/${userId}`);
    return response.data;
  },

  deleteUser: async (userId: string) => {
    const response = await apiClient.delete(`/admin/users/${userId}`);
    return response.data;
  },

  updateUserAdmin: async (userId: string, isAdmin: boolean) => {
    const response = await apiClient.put(`/admin/users/${userId}/admin`, {
      is_admin: isAdmin,
    });
    return response.data;
  },

  createSingleUser: async (
    clientId: string,
    payload: CreateSingleUserRequest
  ): Promise<CreateSingleUserResponse> => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/users/create`,
      payload
    );
    return response.data;
  },

  previewBulkUserUpload: async (
    clientId: string,
    users: BulkUploadUserInput[]
  ): Promise<BulkUploadPreviewResponse> => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/users/bulk-upload/preview`,
      { users }
    );
    return response.data;
  },

  confirmBulkUserUpload: async (
    clientId: string,
    payload: {
      users: BulkUploadUserInput[];
      delivery: BulkUploadDelivery;
      login_url?: string;
    }
  ): Promise<BulkUploadConfirmResponse> => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/users/bulk-upload/confirm`,
      payload
    );
    return response.data;
  },

  assignUserToDepartment: async (departmentId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/departments/${departmentId}/users/${userId}`
    );
    return response.data;
  },

  assignUserToJobRole: async (jobRoleId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/job-roles/${jobRoleId}/users/${userId}`
    );
    return response.data;
  },

  updateUserServices: async (
    userId: string,
    payload: Partial<ServiceOverrides>
  ) => {
    const response = await apiClient.put(`/admin/users/${userId}/services`, payload);
    return response.data;
  },

  resetUserPassword: async (
    userId: string,
    payload: {
      temporary: boolean;
      send_email: boolean;
      login_url?: string;
      password?: string;
    }
  ): Promise<ResetUserPasswordResponse> => {
    const response = await apiClient.post(
      `/admin/users/${userId}/reset-password`,
      payload
    );
    return response.data;
  },

  listRegions: async (clientId: string): Promise<ClientRegionsResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/regions`
    );
    return response.data;
  },

  createRegion: async (clientId: string, payload: CreateRegionRequest) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/regions`,
      payload
    );
    return response.data;
  },

  updateRegion: async (
    regionId: string,
    payload: Partial<CreateRegionRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/regions/${regionId}`,
      payload
    );
    return response.data;
  },

  deleteRegion: async (regionId: string) => {
    const response = await apiClient.delete(`/admin/regions/${regionId}`);
    return response.data;
  },

  assignUserToRegion: async (regionId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/regions/${regionId}/users/${userId}`
    );
    return response.data;
  },

  removeUserFromRegion: async (regionId: string, userId: string) => {
    const response = await apiClient.delete(
      `/admin/regions/${regionId}/users/${userId}`
    );
    return response.data;
  },

  listCentralRegions: async (
    clientId?: string | null
  ): Promise<CentralRegionsResponse> => {
    const response = await apiClient.get("/admin/central/regions", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listDepots: async (clientId: string): Promise<ClientDepotsResponse> => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/depots`
    );
    return response.data;
  },

  createDepot: async (clientId: string, payload: CreateDepotRequest) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/depots`,
      payload
    );
    return response.data;
  },

  updateDepot: async (
    depotId: string,
    payload: Partial<CreateDepotRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/depots/${depotId}`,
      payload
    );
    return response.data;
  },

  deleteDepot: async (depotId: string) => {
    const response = await apiClient.delete(`/admin/depots/${depotId}`);
    return response.data;
  },

  assignUserToDepot: async (depotId: string, userId: string) => {
    const response = await apiClient.post(
      `/admin/depots/${depotId}/users/${userId}`
    );
    return response.data;
  },

  removeUserFromDepot: async (depotId: string, userId: string) => {
    const response = await apiClient.delete(
      `/admin/depots/${depotId}/users/${userId}`
    );
    return response.data;
  },

  listCentralDepots: async (
    clientId?: string | null
  ): Promise<CentralDepotsResponse> => {
    const response = await apiClient.get("/admin/central/depots", {
      params: clientId ? { client_id: clientId } : undefined,
    });
    return response.data;
  },

  listDefaultRegions: async (): Promise<DefaultRegionsResponse> => {
    const response = await apiClient.get("/admin/default/regions");
    return response.data;
  },

  createDefaultRegion: async (payload: CreateRegionRequest) => {
    const response = await apiClient.post("/admin/default/regions", payload);
    return response.data;
  },

  updateDefaultRegion: async (
    regionId: string,
    payload: Partial<CreateRegionRequest>
  ) => {
    const response = await apiClient.put(
      `/admin/default/regions/${regionId}`,
      payload
    );
    return response.data;
  },

  deleteDefaultRegion: async (regionId: string) => {
    const response = await apiClient.delete(
      `/admin/default/regions/${regionId}`
    );
    return response.data;
  },
};
